import { motion } from 'framer-motion'
import { AnimatedLineChart, AnimatedAreaChart, AnimatedPieChart, AnimatedBarChart } from './AnimatedCharts'
import { TrendingUp, PieChart, BarChart3, Activity } from 'lucide-react'

const ChartShowcase = () => {
  const charts = [
    {
      icon: TrendingUp,
      title: 'Trend Analyse',
      description: 'Verfolge deine Ausgaben- und Spartrends über Monate hinweg',
      chart: <AnimatedLineChart />,
      color: 'blue'
    },
    {
      icon: Activity,
      title: 'Cashflow Übersicht',
      description: 'Visualisiere Einnahmen vs. Ausgaben in Echtzeit',
      chart: <AnimatedAreaChart />,
      color: 'green'
    },
    {
      icon: PieChart,
      title: 'Kategorien',
      description: 'Analysiere deine Ausgaben nach Kategorien',
      chart: <AnimatedPieChart />,
      color: 'purple'
    },
    {
      icon: BarChart3,
      title: 'Monatsvergleich',
      description: 'Vergleiche Ausgaben Monat für Monat',
      chart: <AnimatedBarChart />,
      color: 'pink'
    }
  ]

  const colorClasses = {
    blue: 'from-blue-600 to-blue-400',
    green: 'from-green-600 to-green-400',
    purple: 'from-purple-600 to-purple-400',
    pink: 'from-pink-600 to-pink-400'
  }

  return (
    <section id="charts" className="py-12 lg:py-16 bg-gradient-to-b from-black via-gray-900 to-black relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(59,130,246,0.1),transparent_50%)]" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_80%,rgba(139,92,246,0.1),transparent_50%)]" />
      
      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <motion.div
            initial={{ scale: 0 }}
            whileInView={{ scale: 1 }}
            viewport={{ once: true }}
            className="inline-block mb-4"
          >
            <div className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-600/20 to-purple-600/20 border border-blue-500/30 px-6 py-2 rounded-full">
              <TrendingUp className="w-4 h-4 text-blue-400" />
              <span className="text-sm font-semibold text-gradient">Intelligente Visualisierung</span>
            </div>
          </motion.div>
          
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-4 text-white">
            Deine Finanzen in <span className="text-gradient">lebendigen Charts</span>
          </h2>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto">
            Alle deine Bankkonten zusammen visualisiert – mit modernsten, animierten Charts die sich in Echtzeit aktualisieren
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-8">
          {charts.map((item, index) => {
            const Icon = item.icon
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="relative group"
              >
                <div className="relative bg-gray-900/80 backdrop-blur-xl border border-gray-700/50 rounded-3xl p-8 overflow-hidden hover:border-blue-500/50 transition-all duration-300">
                  {/* Glow effect */}
                  <div className="absolute inset-0 bg-gradient-to-br from-blue-600/0 via-purple-600/0 to-blue-600/0 group-hover:from-blue-600/5 group-hover:via-purple-600/5 group-hover:to-blue-600/5 transition-all duration-500" />
                  
                  {/* Header */}
                  <div className="relative z-10 mb-6">
                    <div className={`inline-flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-r ${colorClasses[item.color as keyof typeof colorClasses]} mb-4`}>
                      <Icon className="w-6 h-6 text-white" />
                    </div>
                    <h3 className="text-2xl font-bold text-white mb-2">{item.title}</h3>
                    <p className="text-gray-400">{item.description}</p>
                  </div>

                  {/* Chart */}
                  <div className="relative z-10 bg-black/30 rounded-xl p-4 border border-gray-800/50 h-56">
                    {item.chart}
                  </div>
                </div>
              </motion.div>
            )
          })}
        </div>

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mt-16"
        >
          <div className="inline-block bg-gradient-to-r from-gray-900/80 to-gray-800/80 backdrop-blur-xl border border-gray-700/50 rounded-2xl p-8">
            <p className="text-lg text-gray-300 mb-4">
              <span className="text-gradient font-semibold">Alle Charts</span> aktualisieren sich automatisch bei jeder neuen Transaktion
            </p>
            <div className="flex flex-wrap gap-3 justify-center">
              <div className="px-4 py-2 rounded-full bg-green-600/10 border border-green-500/20">
                <span className="text-sm text-green-400">✓ Echtzeit Updates</span>
              </div>
              <div className="px-4 py-2 rounded-full bg-blue-600/10 border border-blue-500/20">
                <span className="text-sm text-blue-400">✓ Interaktiv</span>
              </div>
              <div className="px-4 py-2 rounded-full bg-purple-600/10 border border-purple-500/20">
                <span className="text-sm text-purple-400">✓ Multi-Banking</span>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  )
}

export default ChartShowcase
